import { useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationsContext';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Notification } from '../types/notifications';

const getNotificationIcon = (type: Notification['type']) => {
  switch (type) {
    case 'success':
      return <CheckCircle size={20} className="text-green-500" />;
    case 'warning':
      return <AlertTriangle size={20} className="text-yellow-500" />;
    case 'error':
      return <XCircle size={20} className="text-red-500" />;
    case 'info':
    default:
      return <Info size={20} className="text-blue-500" />;
  }
};

export default function Notifications() {
  const { notifications, markNotificationAsRead } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => {
    // Mark all notifications as read when viewing the page
    notifications.forEach((notification) => {
      if (!notification.read) {
        markNotificationAsRead(notification.id);
      }
    });
  }, [notifications, markNotificationAsRead]);

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-semibold text-gray-900">Notificações</h1>
        </div>

        <div className="divide-y divide-gray-200">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nenhuma notificação recebida
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-6 hover:bg-gray-50 transition-colors cursor-pointer ${
                  notification.link ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-medium text-gray-900">
                        {notification.title}
                      </h2>
                      <span className="text-sm text-gray-500">
                        {formatDistanceToNow(new Date(notification.timestamp), {
                          addSuffix: true,
                          locale: ptBR
                        })}
                      </span>
                    </div>
                    <p className="mt-1 text-gray-600 whitespace-pre-wrap">
                      {notification.content}
                    </p>
                    {notification.link && (
                      <span className="mt-2 text-sm text-blue-600 hover:text-blue-800">
                        Clique para ver mais detalhes
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
